import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccessManagementUserCollection,
  AccessManagementUserCollectionRecord,
  AccessManagementUserCollectionUserRecord,
} from '@cdw-ae/common-data';
import {
  ApiResponse,
  ICdwSearchGridDataService,
} from '@cdw-ae/common-ui-components-search-grid';
import {
  IGetSqlDataParams,
  SelectedFilterField,
  SqlResponse,
} from '@cdw-ae/filter-bar-interfaces';
import { USER_COLLECTIONS_ROUTES } from '@cdw-ae/routes-access-api';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';
import { httpOptions } from '../designations/designations-data.service';

@Injectable({
  providedIn: 'root',
})
export class UserCollectionsDataService
  implements ICdwSearchGridDataService<AccessManagementUserCollectionRecord>
{
  constructor(
    private httpClient: HttpClient,
    _accessManagementAuthService: AccessManagementFrontendAuthService,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public fetchSqlData(
    params: IGetSqlDataParams
  ): Observable<SqlResponse<AccessManagementUserCollectionRecord>> {
    const { activeFilters, startRow, endRow }: IGetSqlDataParams = params;
    return this.getUserCollections(
      activeFilters,
      startRow,
      endRow
    );
  }

  public getUserCollections(
    activeFilters?: SelectedFilterField[],
    startRow?: number,
    endRow?: number
  ): Observable<SqlResponse<AccessManagementUserCollectionRecord>> {
    const url = this.urlBuilder.backendApi('user-collections', USER_COLLECTIONS_ROUTES.ROOT);
    const queryString = getQueryStringFromParams({
      activeFilters,
      startRow,
      endRow,
    });
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.httpClient.get<SqlResponse<AccessManagementUserCollectionRecord>>(
      fullUrl
    ).pipe(
      catchError(() => of({ data: [], itemTotal: 0 } as SqlResponse<AccessManagementUserCollectionRecord>))
    );
  }

  public getUserCollectionById(
    userCollectionId: number
  ): Observable<{
    success: boolean;
    data: AccessManagementUserCollectionRecord & {
      userCollectionUsers: AccessManagementUserCollectionUserRecord[];
    };
  }> {
    const url = this.urlBuilder.backendApi('user-collections', USER_COLLECTIONS_ROUTES.GET_BY_ID, { id: userCollectionId });
    return this.httpClient
      .get<
        AccessManagementUserCollectionRecord & {
          userCollectionUsers: AccessManagementUserCollectionUserRecord[];
        }
      >(
        url,
        httpOptions
      )
      .pipe(
        map((res) => ({
          success: true,
          data: res,
        })),
        catchError(() => of({ success: false, data: null as never }))
      );
  }

  public updateUserCollection(
    newUserCollection: Omit<AccessManagementUserCollection, 'customerId'>,
    userCollectionUserIds: number[],
    userCollectionId: number
  ): Promise<Observable<ApiResponse<AccessManagementUserCollectionRecord>>> {
    const url = this.urlBuilder.backendApi('user-collections', USER_COLLECTIONS_ROUTES.GET_BY_ID, { id: userCollectionId });
    return Promise.resolve(
      this.httpClient
        .put<AccessManagementUserCollectionRecord>(
          url,
          { name: newUserCollection.name, description: newUserCollection.description, userCollectionUsers: newUserCollection.userCollectionUsers, userCollectionUserIds: userCollectionUserIds },
          httpOptions
        )
        .pipe(
          map(
            (res): ApiResponse<AccessManagementUserCollectionRecord> => ({
              success: true,
              data: [res],
            })
          ),
          catchError(() => of({ success: false, data: [] } as ApiResponse<AccessManagementUserCollectionRecord>))
        )
    );
  }

  public canDeleteUserCollection(userCollectionId: number): Observable<{ canDelete: boolean; message?: string }> {
    const url = this.urlBuilder.backendApi('user-collections', USER_COLLECTIONS_ROUTES.CAN_DELETE, { id: userCollectionId });
    return this.httpClient.get<{ canDelete: boolean; message?: string }>(
      url,
      httpOptions
    ).pipe(
      catchError(() => of({ canDelete: false }))
    );
  }

  public deleteUserCollection(
    userCollectionId: number
  ): Promise<Observable<{ success: boolean }>> {
    const url = this.urlBuilder.backendApi('user-collections', USER_COLLECTIONS_ROUTES.GET_BY_ID, { id: userCollectionId });
    return Promise.resolve(
      this.httpClient.delete<{ success: boolean }>(
        url,
        {
          ...httpOptions,
          body: { userCollectionId },
        }
      ).pipe(
        catchError(() => of({ success: false }))
      )
    );
  }

  public addUserCollection(
    newUserCollection: Omit<AccessManagementUserCollection, 'customerId'>,
    userCollectionUserIds: number[]
  ): Observable<ApiResponse<AccessManagementUserCollectionRecord>> {
    const url = this.urlBuilder.backendApi('user-collections', USER_COLLECTIONS_ROUTES.ROOT);
    return this.httpClient
      .post(
        url,
        { name: newUserCollection.name, description: newUserCollection.description, userCollectionUserIds: userCollectionUserIds },
        httpOptions
      )
      .pipe(
        map((res) => ({
          success: true,
          data: [res as AccessManagementUserCollectionRecord],
        })),
        catchError(() => of({ success: false, data: [] as AccessManagementUserCollectionRecord[] }))
      );
  }
}
